import React, { useState } from 'react';
import { Wand2, Copy, Check } from 'lucide-react';

type WebsiteType = 'blog' | 'ecommerce' | 'business' | 'portfolio';

interface MetaTitle {
  title: string;
  length: number;
}

const generateMetaTitles = (content: string, type: WebsiteType, keywords: string): MetaTitle[] => {
  const templates = {
    blog: [
      "Ultimate Guide: {keyword} - {content}",
      "{content}: Expert Tips & Strategies {year}",
      "How to {keyword}: {content} Guide",
      "{content} - Everything You Need to Know",
      "Top {content} Tips for {keyword}"
    ],
    ecommerce: [
      "Buy {content} | {keyword} Store",
      "{content} - Best {keyword} Collection {year}",
      "Shop {content} - Premium {keyword}",
      "{content} for Sale | {keyword} Deals",
      "Best {content} - {keyword} Store"
    ],
    business: [
      "{content} - Professional {keyword} Services",
      "Leading {keyword} Solutions | {content}",
      "{content} - Expert {keyword} Company",
      "Trusted {keyword} Provider | {content}",
      "{content} - Innovation in {keyword}"
    ],
    portfolio: [
      "{content} - {keyword} Portfolio",
      "Professional {keyword} | {content}",
      "{content} - Creative {keyword} Work",
      "Experienced {keyword} - {content}",
      "{content} - Innovative {keyword} Design"
    ]
  };

  const currentYear = new Date().getFullYear();
  const selectedTemplates = templates[type];
  
  return selectedTemplates.map(template => {
    let title = template
      .replace(/{content}/g, content)
      .replace(/{keyword}/g, keywords)
      .replace(/{year}/g, currentYear.toString());
    
    // Ensure title doesn't exceed 60 characters
    if (title.length > 60) {
      title = title.substring(0, 57) + '...';
    }
    
    return {
      title,
      length: title.length
    };
  });
};

const MetaGenerator: React.FC = () => {
  const [content, setContent] = useState('');
  const [type, setType] = useState<WebsiteType>('blog');
  const [keywords, setKeywords] = useState('');
  const [generatedTitles, setGeneratedTitles] = useState<MetaTitle[]>([]);
  const [copied, setCopied] = useState<number | null>(null);

  const handleGenerate = () => {
    if (content.trim() && keywords.trim()) {
      const titles = generateMetaTitles(content.trim(), type, keywords.trim());
      setGeneratedTitles(titles);
    }
  };

  const copyToClipboard = async (title: string, index: number) => {
    try {
      await navigator.clipboard.writeText(title);
      setCopied(index);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <section id="meta-generator" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="bg-emerald-100 text-emerald-800 px-4 py-1 rounded-full text-sm font-medium">
            AI-Powered Meta Title Generation
          </span>
        </div>
        
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          Meta Title Generator
        </h2>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Website Type
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as WebsiteType)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white"
              >
                <option value="blog">Blog</option>
                <option value="ecommerce">E-commerce</option>
                <option value="business">Business</option>
                <option value="portfolio">Portfolio</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Keywords
              </label>
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="Main keywords for your page"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Page Content/Topic
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Describe your page content"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white h-32"
            />
          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={handleGenerate}
              disabled={!content.trim() || !keywords.trim()}
              className={`bg-emerald-600 text-white py-4 px-8 rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2 ${
                !content.trim() || !keywords.trim() ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <Wand2 className="w-5 h-5" />
              Generate Titles
            </button>
          </div>

          {generatedTitles.length > 0 && (
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                Generated Meta Titles:
              </h3>
              <div className="space-y-4">
                {generatedTitles.map((title, index) => (
                  <div
                    key={index}
                    className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-lg mb-1">{title.title}</p>
                        <p className={`text-sm ${
                          title.length > 60 ? 'text-red-500' : 'text-emerald-600'
                        }`}>
                          {title.length} characters
                        </p>
                      </div>
                      <button
                        onClick={() => copyToClipboard(title.title, index)}
                        className="ml-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        {copied === index ? (
                          <Check className="w-5 h-5 text-emerald-500" />
                        ) : (
                          <Copy className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 text-center text-sm text-gray-500">
            <p className="mb-2">Pro tip: Keep your meta titles between 50-60 characters for optimal display in search results!</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MetaGenerator;